import { useMemo } from "react";
import { localizedIdentification } from "@/lib/localizedId";
import { idNative, idEndangered } from "@/lib/idCompat";
import {
  matchesFilters,
  isUnidentified,
  normalize,
  buildSearchHaystack,
} from "@/lib/galleryUtils";

/**
 * Filter + sort pipeline for the Gallery.
 *
 * Extracted from `Gallery.jsx` unchanged (comparator behavior, needs-id
 * grouping, stable secondary sort tie-breaker, tag AND-match semantics all
 * preserved) so the two-level sort + search + filter + tag pipeline lives
 * in one testable place.
 *
 * @returns filtered+sorted array of scan items, or `null` while `items` is
 *          still loading.
 */
export default function useGalleryFilteredSorted({
  items,
  query,
  filters,
  activeTags,
  sortKey,
  sortKey2,
  userLanguage,
}) {
  return useMemo(() => {
    if (!items) return null;
    const q = normalize(query);
    const matched = items.filter((it) => {
      const id = localizedIdentification(it, userLanguage) || {};
      if (!matchesFilters(it, id, filters, idNative, idEndangered)) return false;
      if (activeTags.size > 0) {
        const itemTagsLower = new Set((it.tags || []).map((t) => String(t).toLowerCase()));
        for (const wanted of activeTags) {
          if (!itemTagsLower.has(wanted)) return false;
        }
      }
      if (!q) return true;
      // Search across the original identification, all cached translations, and tags.
      // Use normalized substring matching so "joe pye weed" matches "Joe-Pye Weed".
      return buildSearchHaystack(it).includes(q);
    });

    // Sort. Keep "needs ID" items grouped at the bottom for name/scientific/type sorts,
    // since their localized common name is "Unknown" / "Desconocido" and shouldn't dominate A–Z.
    const collator = new Intl.Collator(userLanguage || "en", { sensitivity: "base", numeric: true });
    const nameOf = (it) => normalize((localizedIdentification(it, userLanguage) || {}).common_name || "");
    const sciOf = (it) => normalize((localizedIdentification(it, userLanguage) || {}).scientific_name || "");
    const dateOf = (it) => {
      const v = it?.created_at;
      if (!v) return 0;
      const t = typeof v === "string" ? Date.parse(v) : (v instanceof Date ? v.getTime() : 0);
      return Number.isFinite(t) ? t : 0;
    };
    // Photo date: prefer manual/EXIF `date_taken`, else fall back to upload
    // date. `hasPhotoDate` lets the photo_date sort keys push items without a
    // real EXIF date to the bottom in BOTH directions (spec: items missing
    // photo_taken_at sort last in both directions).
    const photoDateOf = (it) => {
      const v = it?.date_taken || it?.created_at;
      if (!v) return 0;
      const t = typeof v === "string" ? Date.parse(v) : (v instanceof Date ? v.getTime() : 0);
      return Number.isFinite(t) ? t : 0;
    };
    const hasPhotoDate = (it) => {
      const v = it?.date_taken;
      if (!v) return false;
      const t = typeof v === "string" ? Date.parse(v) : (v instanceof Date ? v.getTime() : 0);
      return Number.isFinite(t) && t > 0;
    };
    // Lower rank = sorts first.
    const typeRank = (it) => {
      if (isUnidentified(it)) return 99;
      const id = localizedIdentification(it, userLanguage) || {};
      const pt = (id.plant_type || "").toLowerCase();
      const native = idNative(id);
      if (native === true && pt !== "weed") return 0;     // native (beneficial/neutral)
      if (pt === "beneficial") return 1;                  // beneficial but not native
      if (native === false && pt !== "weed") return 2;    // non-native, not weed
      if (pt === "weed") return 3;                        // weed
      return 4;                                           // unknown
    };

    // Comparator factory — one place to build a comparator for any sort key.
    // Primary sort AND secondary sort both use this so the two-level sort is
    // just `primaryCmp(a,b) || secondaryCmp(a,b)`. "none" returns a no-op
    // comparator (0) so the stable Array.prototype.sort preserves existing
    // order — that's what users expect when they pick "no secondary sort".
    const makeCmp = (key) => {
      switch (key) {
        case "none":
          return () => 0;
        case "name_desc":
          return (a, b) => {
            const ua = isUnidentified(a), ub = isUnidentified(b);
            if (ua !== ub) return ua ? 1 : -1;
            return collator.compare(nameOf(b), nameOf(a));
          };
        case "scientific_asc":
          return (a, b) => {
            const ua = isUnidentified(a), ub = isUnidentified(b);
            if (ua !== ub) return ua ? 1 : -1;
            return collator.compare(sciOf(a), sciOf(b));
          };
        case "scientific_desc":
          return (a, b) => {
            const ua = isUnidentified(a), ub = isUnidentified(b);
            if (ua !== ub) return ua ? 1 : -1;
            return collator.compare(sciOf(b), sciOf(a));
          };
        case "date_new":
          return (a, b) => dateOf(b) - dateOf(a);
        case "date_old":
          return (a, b) => dateOf(a) - dateOf(b);
        case "photo_date_new":
          return (a, b) => {
            const ha = hasPhotoDate(a), hb = hasPhotoDate(b);
            if (ha !== hb) return ha ? -1 : 1; // no-date sinks last
            return photoDateOf(b) - photoDateOf(a);
          };
        case "photo_date_old":
          return (a, b) => {
            const ha = hasPhotoDate(a), hb = hasPhotoDate(b);
            if (ha !== hb) return ha ? -1 : 1; // no-date sinks last
            return photoDateOf(a) - photoDateOf(b);
          };
        case "type":
          return (a, b) => typeRank(a) - typeRank(b);
        case "keystone_first":
          return (a, b) => {
            const ka = (localizedIdentification(a, userLanguage) || {}).is_keystone ? 0 : 1;
            const kb = (localizedIdentification(b, userLanguage) || {}).is_keystone ? 0 : 1;
            return ka - kb;
          };
        case "endangered_first":
          return (a, b) => {
            const ea = idEndangered(localizedIdentification(a, userLanguage) || {}) ? 0 : 1;
            const eb = idEndangered(localizedIdentification(b, userLanguage) || {}) ? 0 : 1;
            return ea - eb;
          };
        case "name_asc":
        default:
          return (a, b) => {
            const ua = isUnidentified(a), ub = isUnidentified(b);
            if (ua !== ub) return ua ? 1 : -1;
            return collator.compare(nameOf(a), nameOf(b));
          };
      }
    };

    const primaryCmp = makeCmp(sortKey);
    // Skip secondary when it's "none" OR would compare against the same key
    // (a same-key tie-breaker is redundant — the primary already exhausted
    // that ordering). Falls back to a name tie-breaker as before for name/
    // scientific/type sorts so the visual grouping stays stable.
    const secondaryCmp = sortKey2 === "none" || sortKey2 === sortKey
      ? makeCmp("name_asc")
      : makeCmp(sortKey2);
    const sorted = [...matched].sort((a, b) => primaryCmp(a, b) || secondaryCmp(a, b));
    return sorted;
  }, [items, query, filters, activeTags, userLanguage, sortKey, sortKey2]);
}
